import { postCommercialOutboundConfirm, postResearchOutboundConfirm, } from "@erp/shared/api/generated/wrappers/outbound";
import { uploadFilesWithMapping, } from "@erp/shared/utils/file/upload-file-with-mapping";
import { S3_FOLDER } from "@erp/shared/api/generated/wrappers/s3";
import { MEMBER_TYPE, } from "@erp/shared/components/ui/Dialog/MemberSearchDialog/member.types";
import { useInvalidateQueries } from "@erp/shared/composables/useInvalidateQueries";
import { useLoginInfo } from "@erp/shared/composables/useLoginInfo";
import { usePageInfo } from "@erp/shared/composables/usePageInfo";
import { FILE_CATEGORY } from "@erp/shared/types/file-category.types";
import { createPhase, setApprovalBody, } from "@erp/shared/components/ui/Approval/utils/set-approval-body";
import { useMutation, useQuery } from "@tanstack/vue-query";
import { showAlert, showResult } from "@erp/shared/utils/dialog";
import { getErrorMessage } from "@erp/shared/utils/error";
import { computed } from "vue";
import { getCommercialEigenStockProducts, getResearchEigenStockProducts, } from "@erp/shared/api/generated/wrappers/product";
import { toValue } from "@erp/shared/utils/to-value";
import { useBasicInfo } from "@erp/shared/composables/useBasicInfo";
import { queries } from "@erp/shared/queries";
/**
 * @description 출고 신청 요청 컴포저블
 */
export const useOutboundRegisterAction = (paramsData) => {
    const paramsDataRef = toValue(paramsData);
    const { id } = useLoginInfo();
    const { invalidateQueries } = useInvalidateQueries();
    const { getProjectId, getSpotId } = useBasicInfo();
    const { isCommercialPage } = usePageInfo();
    /**
     * @description 출고 신청 파일 업로드
     * @param formData 출고 신청 폼 데이터
     */
    const setOutboundFile = async (formData) => {
        const uploadTasks = [
            {
                file: formData[FILE_CATEGORY.시험성적서],
                category: FILE_CATEGORY.시험성적서,
                path: S3_FOLDER.OUTBOUND_INSPECTION_REPORT,
            },
            {
                file: formData[FILE_CATEGORY.기타],
                category: FILE_CATEGORY.기타,
                path: S3_FOLDER.OUTBOUND_ETC,
            },
        ].filter((task) => task.file);
        const { successfulFiles, failedFiles } = await uploadFilesWithMapping(uploadTasks);
        return { successfulFiles, failedFiles };
    };
    const setPostOutboundBody = ({ outboundType, memberInfo, formData, outboundItems, fileData, }) => {
        const creater = id.value;
        const checker = memberInfo.find((member) => member.type === MEMBER_TYPE.확인)?.id;
        const approver = memberInfo.find((member) => member.type === MEMBER_TYPE.승인)?.id;
        if (!approver || !checker) {
            throw new Error("승인자 혹은 확인자 정보가 없습니다.");
        }
        const phase = createPhase(creater, checker, approver);
        const spotName = formData.spot ?? null;
        const spotId = spotName ? (getSpotId(spotName) ?? null) : null;
        const postOutboundBody = {
            ...setApprovalBody({
                phase: phase,
                memberInfo: memberInfo,
            }),
            addData: outboundType,
            outboundDate: new Date(formData.outboundDate + "T" + formData.outboundTime),
            outboundPlace: spotName,
            outboundSpotId: spotId,
            purpose: formData.purpose,
            inspectionReportFile: fileData?.[FILE_CATEGORY.시험성적서]?.s3FileName ||
                null,
            etcFiles: fileData?.[FILE_CATEGORY.기타]?.s3FileName || [],
            projectId: getProjectId(formData.projectCode) ?? null,
            note: formData.note,
            products: outboundItems.map((item) => ({
                productId: item.id,
                manufacturerId: item.manufacturerId,
                classificationId: item.classificationId,
                num: Number(item.num * -1),
                unitPrice: item.unitPrice,
                spotId: item.spotId,
                conditions: item.conditions ?? null,
            })),
        };
        return postOutboundBody;
    };
    const readEigenStockProducts = useQuery({
        queryKey: computed(() => ["getEigenStockProducts", isCommercialPage.value]),
        queryFn: () => {
            return isCommercialPage.value
                ? getCommercialEigenStockProducts({
                    ...paramsDataRef.value,
                    hasStock: true,
                })
                : getResearchEigenStockProducts({
                    ...paramsDataRef.value,
                    hasStock: true,
                });
        },
    });
    /**
     * @description 출고 신청 요청
     */
    const createOutboundConfirm = useMutation({
        mutationFn: async ({ body }) => {
            return isCommercialPage.value
                ? await postCommercialOutboundConfirm(body)
                : await postResearchOutboundConfirm(body);
        },
        onSuccess: () => {
            invalidateQueries([
                queries.outbound._def,
                queries.project._def,
                queries.stock._def,
                queries.product._def,
            ]);
            showResult("출고 신청 완료되었습니다", "완료", false, "확인", "닫기");
        },
        onError: (err) => {
            showAlert(getErrorMessage(err), "출고 신청 실패");
        },
    });
    return {
        setOutboundFile,
        setPostOutboundBody,
        createOutboundConfirm,
        readEigenStockProducts,
    };
};
